<script setup lang="ts">
import type { ReelFeedItem, ReelItem, VideoType } from './types';
import type { CarouselApi } from '~/components/ui/carousel';

import {
	Carousel,
	CarouselContent,
	CarouselItem
} from '~/components/ui/carousel';

import IconMaterialSymbolsLightPlayArrow from '~icons/material-symbols-light/play-arrow';

import PropertyReelsFeedItem from './PropertyReelsFeedItem.vue';
import ShortsBottomSheet from './ShortsBottomSheet.vue';
import ShortsHeader from './ShortsHeader.vue';
import {
	getReelFeedItemKey,
	isFeedPhotosItem,
	isReelCustomItem
} from './types';
import { useShortsNeighborGate } from './useShortsUiGate';

interface ShortsVideoFeedProps {
	items: ReelFeedItem[];
	isReady: boolean;
	isLoading: boolean;
	currentIndex: number;
	isActive: boolean;
	autoplayArmed: boolean;
	isVideoEffectivelyPaused: boolean;
	isGlobalMuted: boolean;
	isMobileDevice: boolean;
	videoType: VideoType;
	isDragEnabled: boolean;
	hideHeaderControls: boolean;
	shouldShowLoadingSkeleton: boolean;
	emptyStateTitle: string;
	emptyStateDescription: string;
	emptyStateActionLabel?: string;
	shouldLoadIndex: (index: number) => boolean;
	isPreloadOnlyIndex: (index: number) => boolean;
}

const props = defineProps<ShortsVideoFeedProps>();

const neighborsRevealed = useShortsNeighborGate();

const hasCarouselContent = computed(() => props.items.length > 0);
const isCurrentItemImageOnly = computed(() => {
	const item = props.items[props.currentIndex];
	return !item || isReelCustomItem(item) || isFeedPhotosItem(item);
});

defineEmits<{
	(e: 'init-api', api: CarouselApi): void;
	(e: 'property-clicked', property: ReelItem['property']): void;
	(e: 'video-loaded', index: number): void;
	(e: 'video-playing', index: number): void;
	(e: 'video-buffered', index: number): void;
	(e: 'tap'): void;
	(e: 'toggle-mute'): void;
	(e: 'close'): void;
	(e: 'empty-state-action'): void;
	(e: 'carousel-image-change', url: string | null): void;
}>();
</script>

<template>
	<div
		class="relative flex h-full w-full flex-col overflow-visible [clip-path:inset(0)] lg:[clip-path:inset(0_-9999px_-9999px_-9999px)] bg-transparent"
	>
		<ShortsHeader
			:is-global-muted="isGlobalMuted"
			:hide-header-controls="hideHeaderControls || isCurrentItemImageOnly"
			@toggle-mute="$emit('toggle-mute')"
			@close="$emit('close')"
		>
			<template #desktop-mode-toggle>
				<slot name="desktop-mode-toggle" />
			</template>
		</ShortsHeader>

		<Carousel
			v-if="isReady && hasCarouselContent"
			orientation="vertical"
			class="w-full flex-1 min-h-0 bg-transparent"
			:opts="{
				align: 'start',
				containScroll: 'trimSnaps',
				dragFree: false,
				skipSnaps: false,
				watchDrag: isDragEnabled,
				duration: 30
			}"
			disable-keyboard
			@init-api="api => $emit('init-api', api)"
		>
			<CarouselContent
				class="h-full gap-6 overflow-visible!"
				main-class="overflow-visible!"
			>
				<CarouselItem
					v-for="(item, index) in items"
					:key="getReelFeedItemKey(item)"
					class="h-full basis-full"
				>
					<slot v-if="isReelCustomItem(item)" :name="item.slot" :item="item" />
					<!-- Antes do primeiro frame so o item ativo monta: vizinhos nao
					     disputam banda (posters, avatares, galerias) com o video. O
					     shell do slide fica para o embla manter layout e indices. -->
					<PropertyReelsFeedItem
						v-else-if="neighborsRevealed || index === currentIndex"
						:item="item"
						:index="index"
						:is-active="
							autoplayArmed &&
							index === currentIndex &&
							isActive &&
							!isVideoEffectivelyPaused
						"
						:is-current="autoplayArmed && index === currentIndex && isActive"
						:should-load="shouldLoadIndex(index)"
						:is-preload-only="isPreloadOnlyIndex(index)"
						:video-type="item.videoType || videoType"
						:is-global-muted="isGlobalMuted"
						:is-mobile="isMobileDevice"
						class="size-full"
						@property-clicked="$emit('property-clicked', $event)"
						@video-loaded="$emit('video-loaded', index)"
						@video-playing="$emit('video-playing', index)"
						@video-buffered="$emit('video-buffered', index)"
						@tap="$emit('tap')"
						@carousel-image-change="
							index === currentIndex && $emit('carousel-image-change', $event)
						"
					/>
				</CarouselItem>
			</CarouselContent>
		</Carousel>

		<div
			v-if="shouldShowLoadingSkeleton"
			class="absolute inset-0 z-50 flex flex-col bg-black"
		>
			<div
				class="absolute inset-0 animate-pulse bg-linear-to-b from-white/5 via-white/10 to-white/5"
			/>

			<div class="absolute inset-x-3 bottom-0 z-30 pb-1">
				<div class="h-1 rounded-full bg-white/20">
					<div class="h-full w-1/3 rounded-full bg-white/40" />
				</div>
			</div>

			<div
				class="absolute inset-x-3 bottom-4 z-20 rounded-2xl bg-black/80 px-3 py-2.5 shadow-xl backdrop-blur-md md:hidden"
			>
				<div class="flex items-center justify-between gap-3">
					<div class="min-w-0 flex-1">
						<div class="h-4 w-20 animate-pulse rounded bg-white/20" />
						<div class="mt-1.5 h-3 w-32 animate-pulse rounded bg-white/15" />
						<div class="mt-1.5 flex gap-2">
							<div class="h-2.5 w-12 animate-pulse rounded bg-white/10" />
							<div class="h-2.5 w-14 animate-pulse rounded bg-white/10" />
						</div>
					</div>
					<div class="flex shrink-0 items-center gap-3">
						<div class="size-9 animate-pulse rounded-full bg-white/10" />
						<div class="size-9 animate-pulse rounded-full bg-white/10" />
						<div class="size-9 animate-pulse rounded-full bg-white/10" />
					</div>
				</div>
			</div>

			<div
				class="absolute inset-x-3 bottom-4 z-20 hidden rounded-2xl bg-black/80 px-4 py-3 shadow-xl backdrop-blur-md md:block"
			>
				<div class="flex items-start justify-between gap-3">
					<div class="min-w-0 flex-1">
						<div class="h-6 w-28 animate-pulse rounded bg-white/20" />
						<div class="mt-2 h-4 w-40 animate-pulse rounded bg-white/15" />
						<div class="mt-3 flex gap-3">
							<div class="h-3 w-14 animate-pulse rounded bg-white/10" />
							<div class="h-3 w-16 animate-pulse rounded bg-white/10" />
							<div class="h-3 w-12 animate-pulse rounded bg-white/10" />
						</div>
					</div>
					<div class="flex shrink-0 items-center gap-3">
						<div class="size-10 animate-pulse rounded-full bg-white/10" />
						<div class="size-10 animate-pulse rounded-full bg-white/10" />
						<div class="size-10 animate-pulse rounded-full bg-white/10" />
					</div>
				</div>
			</div>

			<div
				class="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-3"
			>
				<div
					class="size-12 animate-spin rounded-full border-3 border-white/20 border-t-white"
				/>
				<span class="text-sm font-medium text-white/60">Carregando...</span>
			</div>
		</div>

		<div
			v-else-if="!isLoading && items.length === 0"
			class="absolute inset-0 z-30 flex items-center justify-center bg-black px-8"
		>
			<div class="flex max-w-xs flex-col items-center gap-4 text-center">
				<div
					class="flex size-16 items-center justify-center rounded-full bg-white/10"
				>
					<IconMaterialSymbolsLightPlayArrow class="size-8 text-white/60" />
				</div>
				<div>
					<h3 class="text-base font-medium text-white">
						{{ emptyStateTitle }}
					</h3>
					<p class="mt-1 text-sm text-white/50">
						{{ emptyStateDescription }}
					</p>
				</div>
				<button
					v-if="emptyStateActionLabel"
					type="button"
					class="mt-2 rounded-full bg-white px-6 py-2 text-sm font-medium text-black transition-colors hover:bg-white/90"
					@click="$emit('empty-state-action')"
				>
					{{ emptyStateActionLabel }}
				</button>
			</div>
		</div>

		<ShortsBottomSheet>
			<slot name="mobile-bottom-sheet" />
		</ShortsBottomSheet>
	</div>
</template>
